// `/dashboard` route — task 7.4 of the dashboard-redesign spec.
//
// This page is the treasurer-facing landing dashboard defined in
// `.kiro/specs/dashboard-redesign/design.md`. It replaces the previous
// financial-records summary and now exclusively wires the presentational
// `<DashboardShell>` (task 7.2) with the tenant's data.
//
// Responsibilities (kept intentionally small — the shell owns composition):
//
//   1. Fetch the tenant's `Building` list via the shared `buildingsApi`.
//      Loading → full-page skeleton (Requirement 3.5, keeps the same page
//      "silhouette"). Error → full-page `<WidgetError>` card with a stable
//      ErrorId per Requirement 14. The `/dashboard/*` route above already
//      requires `<SignedIn>`, so an unauthenticated request never reaches
//      here; a 401/403 from the API is treated as an error card, not a
//      redirect.
//
//   2. Resolve the hashed session key via `useHashedSessionKey`. This is
//      the tenant-safe identifier used to namespace persisted scope in
//      localStorage (Requirement 2.5). Raw `ownerId` is never touched.
//
//   3. Hold the current `BuildingScope` in local state. Default is the
//      aggregate view `{ kind: "all" }` (Requirement 2.1). On mount the
//      `useState` initializer reads any persisted value; because
//      `useHashedSessionKey` returns `undefined` on first render, the
//      initializer reliably returns `null` and we fall back to the
//      default — the rehydration effect below then restores the persisted
//      scope once the hash resolves (Requirement 2.6).
//
//   4. Delegate rendering to `<DashboardShell>` which composes the header,
//      onboarding banner, four bands of widgets, and the reserved chatbot
//      launcher slot. The shell also mounts `<BuildingSelector>` which
//      persists new picks back to localStorage on change (task 7.1).
//
// This file does NOT read `ownerId`, fetch financial data, or render any
// monetary value directly — that is the widgets' job. The old dashboard's
// stat cards, chart.js integration, and BuildingContext sentinels
// (`__ALL__` / `COMPANY`) are intentionally removed: task 7.4 replaces
// the page entirely.

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Building } from "@akfdev-com/shared";

import { buildingsApi } from "../services/api";
import { useHashedSessionKey } from "../auth/useHashedSessionKey";
import { DashboardShell } from "./dashboard/DashboardShell";
import { readPersistedScope } from "./dashboard/BuildingSelector";
import { WidgetError } from "./dashboard/WidgetError";
import type { BuildingScope } from "./dashboard/types";

/** Default scope when no persisted value exists (Requirement 2.6 fallback). */
const DEFAULT_SCOPE: BuildingScope = { kind: "all" };

/**
 * Runtime guard for the `buildingsApi.getAll()` response. The API returns
 * an array of `Building` records; anything else (unexpected 200 body, network
 * middleware error) is treated as an empty list so the shell can show the
 * onboarding banner rather than crashing.
 */
function isBuildingArray(value: unknown): value is Building[] {
  return Array.isArray(value);
}

/**
 * Full-page loading skeleton. Bounding boxes deliberately mirror the shell's
 * final grid (Requirement 3.5) so users don't see a layout shift when data
 * arrives. Uses only Tailwind's `animate-pulse` — no bespoke keyframes.
 */
function DashboardLoadingSkeleton(): JSX.Element {
  return (
    <div
      data-testid="dashboard-loading"
      className="p-6 space-y-6 min-h-screen animate-pulse"
      style={{ backgroundColor: "#f3f4f6" }}
    >
      <div className="h-10 w-64 rounded-md bg-gray-200" />
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={`kpi-${i}`} className="h-28 rounded-xl bg-gray-200" />
        ))}
      </div>
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={`mid-${i}`} className="h-64 rounded-xl bg-gray-200" />
        ))}
      </div>
      <div className="h-56 rounded-xl bg-gray-200" />
    </div>
  );
}

/**
 * Full-page error card shown when the tenant's building list cannot be
 * fetched. Reuses `<WidgetError>` so the visual language matches the widget
 * error state; the `errorId` uses a stable synthetic value (`"buildings-fetch"`)
 * because the underlying axios error is already surfaced to CloudWatch by the
 * shared error handler in `services/api.ts`.
 */
function DashboardErrorCard({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <div
      data-testid="dashboard-error"
      className="p-6 min-h-screen"
      style={{ backgroundColor: "#f3f4f6" }}
    >
      <div className="mx-auto max-w-md">
        <WidgetError
          name="Dashboard"
          errorId="buildings-fetch"
          onRetry={onRetry}
        />
      </div>
    </div>
  );
}

/**
 * The `/dashboard` index route. Renders one of three states:
 *   1. Loading — full-page skeleton while `useQuery(["buildings"])` is in
 *      flight.
 *   2. Error — full-page `<WidgetError>` with a Retry button that reloads.
 *   3. Ready — `<DashboardShell>` with the resolved scope, buildings, and
 *      hashed session key.
 *
 * The page has NO `<Layout>` wrapper of its own — the parent route in
 * `App.tsx` already wraps every `/dashboard/*` element in `<Layout>` and
 * `<DocsSliderShellWrapper>`.
 */
export function Dashboard(): JSX.Element {
  const sessionKey = useHashedSessionKey();

  const {
    data: buildingsData,
    isLoading: buildingsLoading,
    error: buildingsError,
  } = useQuery<unknown>(["buildings"], () => buildingsApi.getAll());

  const buildings: readonly Building[] = isBuildingArray(buildingsData)
    ? buildingsData
    : [];

  // Initial scope: fall back to aggregate. The initializer runs BEFORE
  // `useHashedSessionKey` resolves the hash, so it always sees
  // `sessionKey === undefined` on first render → `readPersistedScope`
  // returns `null` → default. The effect below runs the real hydration.
  const [scope, setScope] = useState<BuildingScope>(() => {
    const persisted = readPersistedScope(sessionKey);
    return persisted ?? DEFAULT_SCOPE;
  });

  // Rehydrate exactly once when the hashed session key becomes available.
  // Guarded by a ref so an unrelated re-render (e.g. buildings arrive) can
  // never clobber a user selection that happened between mount and hash
  // resolution.
  const rehydratedRef = useRef<boolean>(false);
  useEffect(() => {
    if (rehydratedRef.current) return;
    if (sessionKey === undefined) return;
    rehydratedRef.current = true;
    const persisted = readPersistedScope(sessionKey);
    if (persisted !== null) {
      setScope(persisted);
    }
  }, [sessionKey]);

  if (buildingsLoading) {
    return <DashboardLoadingSkeleton />;
  }

  if (buildingsError) {
    return <DashboardErrorCard onRetry={() => window.location.reload()} />;
  }

  return (
    <DashboardShell
      scope={scope}
      onScopeChange={setScope}
      buildings={buildings}
      sessionKey={sessionKey}
    />
  );
}

export default Dashboard;
